<template>
  <div
    v-outside-click="removeScope"
    class="kk__findReplace"
    :style="{ top: `${boxPosition.top}px`, right: `${boxPosition.right}px` }"
    @click="setScope"
  >
    <div class="kk__find" @mousedown="startDrag">
      <i class="icon-search kk__find--search"></i>
      <input
        ref="findInput"
        v-model="searchText"
        type="text"
        class="kk__find--input"
        placeholder="Find"
        @keydown="handleInputKeyDown"
        @input="change"
        @click="updateFind"
      />
      <span v-if="searchText" class="kk__find--foundMatch" :class="{ visible: searchText }"
        >{{ currentMatchIndex + 1 }}/{{ allMatches }}</span
      >
      <i class="icon-sorting-go-to kk__find--expand" @click="toggleReplace"></i>
      <i class="icon-close kk__find--close" @click="close"></i>
    </div>

    <div v-if="showReplace" class="kk__replace">
      <div class="kk__replaceInputWrapper">
        <fieldset>
          <legend>Replace With</legend>
          <input
            ref="replaceInput"
            v-model="replaceText"
            type="text"
            placeholder="Replace With"
            class="kk__replace--input"
            @keydown="handleInputKeyDown"
            @click="handleClick"
          />
        </fieldset>
        <span v-if="showReplaceAll" class="kk__find--replaceMatch">{{ totalFound }}/{{ totalReplaced }}</span>
      </div>

      <div
        class="kk__replace--search"
        tabindex="0"
        role="searchOption"
        :class="{ active: showReplaceOptions }"
        @click="toggleReplaceOptions"
      >
        {{ $t("findReplace.searchOptions") }} <i class="icon-Dropdown-2"></i>
      </div>

      <div v-if="showReplaceOptions" class="kk__replace--options">
        <div class="kk__replace--optionsWrapper">
          <div v-if="isTypeGrid" class="kk__replace--optionsDropdown">
            <div>{{ $t("findReplace.within") }}</div>
            <div class="kk__replace--optionsSelect" :class="{ active: isProcessActive }">
              <div
                class="kk__replace--optionsBtn kk__within"
                tabindex="0"
                role="within"
                @click="toggleProcess"
                @blur="isProcessActive = false"
              >
                <span class="kk__replace--optionsText">{{ selectedProcess }}</span>
                <i class="icon-menu-down"></i>
              </div>

              <ul v-if="isProcessActive" class="kk__replace--optionsLists">
                <li
                  v-for="(option, index) in processes"
                  :key="index"
                  class="kk__replace--optionsList"
                  :class="{
                    'kk__replace--selected': option.selected,
                    'kk__replace--activeItem': index === activeProcess,
                  }"
                  @click="selectProcess(option)"
                >
                  {{ option.text }}
                </li>
              </ul>
            </div>
          </div>

          <div class="kk__replace--optionsDropdown">
            <div>{{ $t("findReplace.search") }}</div>
            <div class="kk__replace--optionsSelect" :class="{ active: isTypeActive }">
              <div
                class="kk__replace--optionsBtn kk__search"
                tabindex="0"
                role="search"
                @click="toggleType"
                @blur="isTypeActive = false"
              >
                <span class="kk__replace--optionsText">{{ selectedType }}</span>
                <i class="icon-menu-down"></i>
              </div>

              <ul v-if="isTypeActive" class="kk__replace--optionsLists">
                <li
                  v-for="(option, index) in types"
                  :key="index"
                  class="kk__replace--optionsList"
                  :class="{
                    'kk__replace--selected': option.selected,
                    'kk__replace--activeItem': index === activeType,
                  }"
                  @click="selectType(option)"
                >
                  {{ option.text }}
                </li>
              </ul>
            </div>
          </div>
          <div class="kk__replace--optionsDropdown">
            <div>{{ $t("findReplace.searchWithin") }}</div>
            <div class="kk__replace--optionsSelect" :class="{ active: isSearchWithinActive }">
              <div
                class="kk__replace--optionsBtn kk__searchWithin"
                tabindex="0"
                role="searchWithin"
                @click="toggleSearchWithin"
                @blur="isSearchWithinActive = false"
              >
                <span class="kk__replace--optionsText">{{ selectedSearchWithin }}</span>
                <i class="icon-menu-down"></i>
              </div>

              <ul v-if="isSearchWithinActive" class="kk__replace--optionsLists">
                <li
                  v-for="(option, index) in searchWithin"
                  :key="index"
                  class="kk__replace--optionsList"
                  :class="{
                    'kk__replace--selected': option.selected,
                    'kk__replace--activeItem': index === activeSearchWithin,
                  }"
                  @click="selectSearchWithin(option)"
                >
                  {{ option.text }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="kk__replace--options--checkbox">
          <label>
            <input ref="matchCaseCheckbox" type="checkbox" role="matchCase" @click="toggleMatchCase" />
            {{ $t("findReplace.matchCase") }}
          </label>

          <label>
            <input ref="wholeWordCheckbox" type="checkbox" role="matchWord" @click="toggleWholeWord" />
            {{ $t("findReplace.matchCell") }}
          </label>
        </div>
      </div>

      <div v-if="showFindAll" class="findReplaceTable">
        <customtable
          :data="tableData"
          :headers="headers"
          :head-style="headStyle"
          :td-bg="bgOnHover"
          :parent-scope="parentScope"
          overflow-value="auto"
          @handleTab="handleTabNavigation"
        />
      </div>

      <div class="kk__replace--buttons">
        <button class="kk__replace--buttons-replace" role="replace" @click="replaceOne">
          {{ $t("findReplace.replace") }}
        </button>
        <button class="kk__replace--buttons-replaceAll" role="replaceAll" @click="replaceAll">
          {{ $t("findReplace.replaceAll") }}
        </button>
        <button class="kk__replace--buttons-find" role="findNext" @click="nextMatch">
          {{ $t("findReplace.findNext") }}
        </button>
        <button class="kk__replace--buttons-findall" role="findAll" @click="toggleFindAll">
          {{ $t("findReplace.findAll") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Preferences } from "../../../utils/constants";
import { cellIndexToLabel } from "../../../utils/util";
import DesignerMixin from "../DesignerMixin";
import hotkeys from "hotkeys-js";
import { HotkeyScopes } from "../../../utils/constants";
import Customtable from "../../commons/Customtable.vue";

export default {
  name: "Find",
  components: { Customtable },
  mixins: [DesignerMixin],

  data() {
    return {
      allResults: 0,
      parentScope: HotkeyScopes.FindReplace,
      currentFocusIndex: null,
      prevScope: null,
      allSheetsData: null,
      searchText: "",
      replaceText: "",
      totalMatches: 0,
      allMatches: 0,
      totalReplaced: 0,
      showReplaceAll: null,
      tabHandled: false,
      totalFound: 0,
      currentMatchIndex: -1,
      filteredResults: {},
      matchKeys: [],
      matchCase: false,
      matchWholeWord: false,
      dataMatrix: [],
      previousMatchIndex: 0,
      showReplace: false,
      showReplaceOptions: false,
      isProcessActive: false, // Controls the visibility of the dropdown
      isTypeActive: false,
      isSearchWithinActive: false,
      activeSearchWithin: 0,
      selectedProcess: "Sheet", // Default text
      selectedType: "By Rows",
      selectedSearchWithin: "Formula",
      isDragging: false,
      offset: { x: 0, y: 0 }, // Offset to calculate the mouse position relative to the box
      activeProcess: 0,

      processes: [
        {
          id: 1,
          text: "Grid",
          selected: false,
        },
        {
          id: 2,
          text: "Sheet",
          selected: false,
        },
      ],
      activeType: 0,
      types: [
        {
          id: 1,
          text: "By Row",
          selected: false,
        },
        {
          id: 2,
          text: "By Column",
          selected: false,
        },
      ],

      tableData: [],
      headers: [
        { label: "Sheet", accessor: "sheet", icon: "icon-menu-down" },
        { label: "Value", accessor: "value", icon: "icon-menu-down" },
        { label: "Cell", accessor: "cell", icon: "icon-menu-down" },
        { label: "Formula", accessor: "formula", icon: "icon-menu-down" },
      ],
      showFindAll: false,
    };
  },

  computed: {
    headStyle() {
      return {
        backgroundColor: "transparent",
      };
    },
    allSheets() {
      return this.$store.state.sheets.allSheets;
    },
    bgOnHover() {
      return { backgroundColor: "rgba(8, 50, 119, 0.13)" };
    },
    isTypeGrid() {
      return this.$store.state.cells.current?.type === "grid";
    },
    boxPosition() {
      return this.$store.state.preferences.allPreferences?.findReplacePosition || { top: 140, right: 33 };
    },
    activeScope() {
      return this.$store.state.activeScope; // Get current scope from Vuex
    },
    redrawCanvas() {
      return this.$store.state.cells.redrawCanvas;
    },
  },

  watch: {
    allSheets(newVal) {
      this.allSheetsData = newVal;
    },
    "currentSheet.name"(newValue, oldValue) {
      this.updateFind();
    },
    redrawCanvas: {
      handler() {
        if (this.redrawCanvas.value) {
          this.change({ target: { value: this.searchText } }, this.redrawCanvas.value);
        }
      },
      immediate: false,
    },
  },

  mounted() {
    this.$refs.findInput.focus();
    this.prevScope = hotkeys.getScope();
    this.$store.dispatch("setScope", HotkeyScopes.FindReplace);
    this.allSheetsData = this.$store.state.sheets.allSheets;

    hotkeys("tab", HotkeyScopes.FindReplace, (event) => {
      event.preventDefault();
      this.handleTabNavigation(event.shiftKey);
    });

    hotkeys("esc", HotkeyScopes.FindReplace, (event) => {
      event.preventDefault();
      this.close();
    });

    hotkeys("space", HotkeyScopes.FindReplace, (event) => {
      event.preventDefault();
      const activeElement = document.activeElement;
      const role = activeElement?.getAttribute("role"); // Get role attribute properly
      switch (role) {
        case "searchOption":
          this.toggleReplaceOptions();
          break;
        case "within":
          this.toggleProcess(event);
          break;
        case "search":
          this.toggleType(event);
          break;
        case "searchWithin":
          this.toggleSearchWithin(event);
          break;
        case "matchCase":
          activeElement.checked = !activeElement.checked;
          this.toggleMatchCase();
          break;
        case "matchWord":
          activeElement.checked = !activeElement.checked;
          this.toggleWholeWord();
          break;
        case "findAll":
          this.toggleFindAll();
          break;
      }
    });

    // Arrow Down: Navigate dropdowns
    hotkeys("down", HotkeyScopes.FindReplace, (event) => {
      if (this.isProcessActive) {
        event.preventDefault();
        this.activeProcess = (this.activeProcess + 1) % this.processes.length;
      }
      if (this.isTypeActive) {
        event.preventDefault();
        this.activeType = (this.activeType + 1) % this.types.length;
      }
      if (this.isSearchWithinActive) {
        event.preventDefault();
        this.activeSearchWithin = (this.activeSearchWithin + 1) % this.searchWithin.length;
      }
    });

    // Arrow Up: Navigate dropdowns
    hotkeys("up", HotkeyScopes.FindReplace, (event) => {
      if (this.isProcessActive) {
        event.preventDefault();
        this.activeProcess = (this.activeProcess - 1 + this.processes.length) % this.processes.length;
      }
      if (this.isTypeActive) {
        event.preventDefault();
        this.activeType = (this.activeType - 1 + this.types.length) % this.types.length;
      }
    });

    // Enter Key: Perform actions based on focus
    hotkeys("enter", HotkeyScopes.FindReplace, (event) => {
      event.preventDefault();
      const activeElement = document.activeElement;
      const role = activeElement?.getAttribute("role"); // Get role attribute properly
      switch (role) {
        case "replace":
          this.replaceOne();
          break;
        case "replaceAll":
          this.replaceAll();
          break;
        case "findNext":
          this.nextMatch();
          break;
        case "findAll":
          this.nextMatch();
          break;
        default:
          // Handle dropdown selections
          if (this.isProcessActive) {
            this.selectProcess(this.processes[this.activeProcess]);
            this.isProcessActive = false;
          } else if (this.isTypeActive) {
            this.selectType(this.types[this.activeType]);
            this.isTypeActive = false;
          } else if (this.isSearchWithinActive) {
            this.selectSearchWithin(this.searchWithin[this.activeSearchWithin]);
            this.isSearchWithinActive = false;
          }
      }
    });
  },

  beforeUnmount() {
    this.removeScope();
  },

  methods: {
    removeScope() {
      this.$store.dispatch("setScope", this.prevScope);
      hotkeys.unbind("*", HotkeyScopes.FindReplace);
    },
    setScope() {
      if (hotkeys.getScope() !== HotkeyScopes.FindReplace) {
        const activeElement = document.activeElement;
        const role = activeElement?.getAttribute("role"); // Get role attribute properly
        if (role === "cusTable") return;
        this.$store.dispatch("setScope", HotkeyScopes.FindReplace);
      }
    },
    handleInputKeyDown(e) {
      switch (e.key) {
        case "Tab":
          e.preventDefault();
          if (!this.showReplace) {
            this.showReplace = true;
            this.tabHandled = true; // Mark Tab as handled
            return;
          } else this.handleTabNavigation(e.shiftKey);
          break;
        case "Enter":
          e.preventDefault();
          if (e.shiftKey) {
            this.previousMatch();
          } else {
            this.nextMatch();
          }
          break;
        case "Escape":
          e.preventDefault();
          this.close();
      }
    },
    toggleFindAll() {
      this.showFindAll = !this.showFindAll;
      this.updateFind();
    },
    handleClick() {
      const focusable = this.getFocusableElements();
      const activeIndex = focusable.indexOf(document.activeElement);
      if (activeIndex !== -1) this.currentFocusIndex = activeIndex;
    },
    handleFocus(index) {
      const role = document.activeElement?.role;
      if (role !== "within" || role !== "search" || role !== "searchWithin") {
        this.isProcessActive = false;
        this.isTypeActive = false;
        this.isSearchWithinActive = false;
      }
      const focusable = this.getFocusableElements();

      if (index < 0) {
        index = focusable.length - 1; // Wrap to the last element if out of bounds
      } else if (index >= focusable.length) {
        index = 0; // Wrap to the first element
      }
      this.currentFocusIndex = index;
      focusable[this.currentFocusIndex]?.focus();
    },

    handleTabNavigation(isShift = false) {
      const focusable = this.getFocusableElements();
      const currentIndex = focusable.indexOf(document.activeElement); // Get current index

      let nextIndex = isShift ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0) nextIndex = focusable.length - 1; // Wrap to last element
      if (nextIndex >= focusable.length) nextIndex = 0; // Wrap to first element

      this.currentFocusIndex = nextIndex;
      this.handleFocus(this.currentFocusIndex);
    },

    getFocusableElements() {
      // Collect all focusable elements in the form
      return [...this.$el.querySelectorAll('button, input, div[tabindex="0"]')].filter(
        (el) => !el.disabled && !el.hasAttribute("disabled")
      );
    },
    startDrag(event) {
      this.isDragging = true;
      // Calculate the offset, but use the window width to calculate `right`
      this.offset.x = window.innerWidth - event.clientX - this.boxPosition.right;
      this.offset.y = event.clientY - this.boxPosition.top;

      // Add event listeners to handle dragging
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.isDragging) return;
      // Update the position of the box
      let top = event.clientY - this.offset.y;
      let right = window.innerWidth - event.clientX - this.offset.x; // Adjust 'right'

      this.$store.dispatch("preferences/updateWithoutPersist", {
        key: Preferences.findReplacePositoin,
        value: {
          top,
          right,
        },
      });
    },
    stopDrag() {
      if (this.isDragging) this.$store.dispatch("preferences/persist");
      this.isDragging = false;
      // Remove the event listeners
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      this.$refs.findInput.focus();
    },
    toggleReplace() {
      this.showReplace = !this.showReplace;
    },
    toggleReplaceOptions() {
      this.showReplaceOptions = !this.showReplaceOptions;
    },
    toggleProcess(event) {
      this.isProcessActive = !this.isProcessActive;
      this.handleClick();
    },

    toggleType() {
      this.isTypeActive = !this.isTypeActive;
      this.handleClick();
    },
    toggleSearchWithin() {
      this.isSearchWithinActive = !this.isSearchWithinActive;
      this.handleClick();
    },
    selectProcess(option) {
      this.selectedProcess = option.text;
      this.isProcessActive = false; // Close the dropdown
      this.processes = this.processes.map((process) => {
        if (process.id == option.id) {
          return { ...process, selected: process.selected ? false : true };
        } else return process;
      });
      this.updateFind();
    },

    selectType(option) {
      this.selectedType = option.text;
      this.isTypeActive = false;
      this.types = this.types.map((type) => {
        if (type.id == option.id) {
          return { ...type, selected: type.selected ? false : true };
        } else return type;
      });
      this.updateFind();
    },

    selectSearchWithin(option) {
      this.selectedSearchWithin = option.text;
      this.isSearchWithinActive = false;
      this.searchWithin = this.searchWithin.map((el) => {
        if (el.id === option.id) {
          return { ...el, selected: el.selected ? false : true };
        } else return el;
      });
    },

    updateFind() {
      this.change({ target: { value: this.searchText } });
      this.handleClick();
      this.setScope();
      this.$refs.findInput?.focus();
    },
    toggleCheckbox(checkboxName) {
      // Toggle the value of the checkbox
      this[checkboxName] = !this[checkboxName];
    },
    toggleMatchCase() {
      this.matchCase = !this.matchCase;
      this.updateFind();
      this.handleClick();
    },

    toggleWholeWord() {
      this.matchWholeWord = !this.matchWholeWord;
      this.updateFind();
      this.handleClick();
    },
    close() {
      this.$store.dispatch("cells/find", "");
      this.$emit("close");
    },
    async change(e, redraw) {
      if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
      }
      const text = e.target?.value || this.searchText;
      if (text.length > 1) {
        this.showReplaceAll = false;
      }

      const result = await this.$store.dispatch("cells/find", {
        text,
        matchCase: this.matchCase,
        matchWholeWord: this.matchWholeWord,
        type: this.selectedType,
        search: this.selectedProcess,
      });
      this.allMatches = result.allMatches;
      this.totalMatches = result.totalMatches;
      this.filteredResults = result.filteredResults;
      this.dataMatrix = result.dataMatrix;
      this.matchKeys = Object.keys(this.filteredResults);
      this.allResults = result.allResults;

      if (result.allResults) {
        this.findAllData(result.allResults);
      } else {
        this.tableData = [];
      }

      if (this.previousMatchIndex >= this.totalMatches) {
        this.currentMatchIndex = this.totalMatches > 0 ? 0 : -1;
      } else {
        this.currentMatchIndex = this.previousMatchIndex;
      }

      if (this.currentMatchIndex !== -1 && !redraw) {
        this.emitCurrentMatch();
      }
    },
    findAllData(findCells) {
      this.tableData = [];

      Object.entries(findCells).forEach(([sheetName, cells]) => {
        const sheet = this.allSheetsData?.find((s) => s.name === sheetName);
        const sheetId = sheet ? sheet.sheetId : null;

        if (sheetId === null) {
          console.warn(`Sheet not found: ${sheetName}`);
          return;
        }

        Object.entries(cells).forEach(([key, _]) => {
          const [rowIndex, colIndex] = key.split("_").map(Number);

          try {
            const text = this.getCellValue(rowIndex, colIndex, sheetId);
            const formula = this.getCellText(rowIndex, colIndex, sheetId);
            const data = {
              sheet: { value: sheetName },
              sheetId: { value: sheetId },
              value: { value: text },
              cell: { value: cellIndexToLabel(colIndex, rowIndex) },
              formula: { value: formula == text ? " " : formula },
            };

            this.tableData.push(data);
          } catch (error) {
            console.error("Error getting cell value:", error);
          }
        });
      });
    },
    nextMatch() {
      if (this.totalMatches > 0) {
        this.currentMatchIndex = (this.currentMatchIndex + 1) % this.totalMatches;
        this.emitCurrentMatch();
      }
      this.handleClick();
    },
    previousMatch() {
      if (this.totalMatches > 0) {
        this.currentMatchIndex = (this.currentMatchIndex - 1 + this.totalMatches) % this.totalMatches;
        this.emitCurrentMatch();
      }
    },
    emitCurrentMatch() {
      const matchKey = this.matchKeys[this.currentMatchIndex];

      if (matchKey) {
        const [rowIndex, colIndex] = matchKey.split("_").map(Number);

        const activeCellId = {
          columnIndex: colIndex,
          rowIndex: rowIndex,
        };
        this.$emit("select-match", activeCellId);
      }
    },
    async replaceOne() {
      if (!this.searchText || !this.replaceText) return;

      const matchKey = this.matchKeys[this.currentMatchIndex];
      if (!matchKey) return;

      const singleResult = {
        [matchKey]: this.filteredResults[matchKey],
      };

      const currentCellContent = this.dataMatrix[this.currentMatchIndex];
      if (!currentCellContent) return;

      await this.$store.dispatch("cells/replace", {
        findText: this.searchText,
        replaceText: this.replaceText,
        filteredResults: this.allResults,
        replaceOne: singleResult,
        dataMatrix: [currentCellContent],
      });

      this.previousMatchIndex = this.currentMatchIndex;
      this.updateFind();
      this.nextMatch();
      this.handleClick();
    },

    async replaceAll() {
      if (!this.searchText || !this.replaceText) return;

      const result = await this.$store.dispatch("cells/replace", {
        findText: this.searchText,
        replaceText: this.replaceText,
        filteredResults: this.allResults,
        replaceOne: null,
        dataMatrix: this.dataMatrix,
        selectedProcess: this.selectedProcess,
      });
      this.totalReplaced = result;
      this.totalFound = this.totalMatches;
      this.updateFind();
      this.handleClick();
      this.showReplaceAll = true;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables.less";

.selected {
  background-color: rgba(0, 123, 255, 0.2);
  cursor: pointer;
}

.kk__replaceInputWrapper {
  position: relative;

  span {
    position: absolute;
    top: 9px;
    right: 10px;
    color: #6e8cbb;
  }

  fieldset {
    height: 40px;
    position: relative;
    border-radius: 5px;
    border: 1px solid @kk-input-border-color;
    padding: 6px 0;

    input {
      width: 100%;
      padding: 0 8px;
      border: none;
      outline: none;

      &::placeholder {
        color: #6e8cbb;
        opacity: 1;
      }
    }
  }

  legend {
    font-size: 1rem;
    position: fixed;
    visibility: hidden;
    padding: 0 3px;
    margin: 0 5px;
    color: @kk-color-royal-blue;
  }

  fieldset:focus-within {
    border-color: @kk-color-royal-blue;
    padding: 0;

    input {
      &::placeholder {
        opacity: 0;
      }
    }

    legend {
      font-size: 0.7rem;
      position: relative;
      visibility: visible;
    }
  }
}

.findReplaceTable {
  color: @kk-color-primary-blue;
  border-collapse: collapse; /* Ensures no extra spacing between rows */
  padding: 21px 0px 0px 21px;
  border: 1px solid @kk-input-border-color;
  border-radius: @dialog-border-radius;
  overflow: scroll;
}

:deep(*:focus) {
  outline: 0.5px solid @kk-corner-header-color;
  transition: outline ease-in-out;
}

input:focus::placeholder {
  color: transparent; /* Hides the placeholder */
}

.kk__findReplace {
  position: fixed;
  min-width: 32rem;
  z-index: 105;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.kk__find--input,
.kk__replace--input {
  color: @kk-color-primary-blue;
  caret-color: rgba(8, 50, 119, 0.5);
  &::placeholder {
    color: #6e8cbb;
  }
}

.kk__find {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(201, 224, 255, 0.8);
  border: 1px solid @kk-input-border-color;
  border-radius: 5px;
  padding: 0px 21px;
  color: #6e8cbb;
  cursor: grab;

  &:focus-within {
    outline: 0.5px solid @kk-corner-header-color;
  }

  &--input {
    flex: 1;
    padding: 8px 0px;
    height: 36px;

    &:focus {
      outline: none;
      border: none;
    }
  }

  &--expand,
  &--close,
  &--search {
    cursor: pointer;
  }

  &--expand,
  &--close {
    font-weight: 600;

    &:hover,
    .kk__find--search:hover {
      color: @kk-color-primary-blue;
    }
  }

  &--search {
    font-weight: 500;
  }
}

.kk__replace--search,
.kk__replace--buttons,
.kk__replace--input,
.kk__find--input,
.kk__find--search {
  font-weight: 500;
}

.kk__replace {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 1rem;
  background: rgba(201, 224, 255, 0.8);
  border: 1px solid rgba(8, 50, 119, 0.25);
  padding: 21px;
  border-radius: 8px;
  color: @kk-color-primary-blue;

  &--search {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    cursor: pointer;
    width: max-content;

    i {
      font-size: 1.3rem;
      transform: rotate(-90deg);
    }
  }

  &--search.active i {
    transform: rotate(0deg);
  }

  &--optionsDropdown {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &--optionsBtn {
    display: flex;
    align-items: center;
    cursor: pointer;
    justify-content: space-between;
    border: 1px solid @kk-input-border-color;
    border-radius: 5px;
    padding: 0 10px;
    padding: 2px 4px;
    // height: 27px;

    &:hover {
      background: #9fb3d3;
      border: 1px solid rgba(159, 179, 211, 1);
    }
  }

  &--optionsSelect {
    position: relative;
  }

  &--optionsText {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &--optionsLists {
    position: absolute;
    right: 0;
    left: 0;
    overflow-y: auto;
    color: @kk-color-primary-blue;
    font-size: 13px;
    border-radius: 5px;
    background: @kk-color-white;
    border: none;
    outline: none;
    z-index: 10;
  }

  &--optionsList:not(:last-child) {
    margin-bottom: 2px;
  }

  &--optionsList {
    cursor: pointer;
    padding: 6px 4px;
    border-radius: 2px;

    &:hover {
      background: @kk-sky-blue;
    }
  }

  &--selected {
    background: @kk-color-primary-blue !important; /* Change to your desired background color */
    color: @kk-color-white;
  }

  &--activeItem {
    background: @kk-sky-blue;
  }

  &--options {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    color: inherit;

    &--checkbox {
      flex: 1;
      display: grid;
      gap: 0.6rem;
      justify-content: end;
      label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      /* Hide the default checkbox */
      input[type="checkbox"] {
        appearance: none; /* Removes the default styling */
        -webkit-appearance: none; /* Safari support */
        width: 16px;
        height: 16px;
        border: 1px solid #98a5bb;
        border-radius: 2px;
        cursor: pointer;
        outline: none;
      }

      /* Checked state */
      input[type="checkbox"]:checked {
        background-color: @kk-color-primary-blue; /* Checkbox color */
        border: none;
      }

      input[type="checkbox"]:focus {
        outline: 0.5px solid @kk-corner-header-color;
      }

      /* Tick styling */
      input[type="checkbox"]:checked::after {
        content: "";
        display: block;
        width: 16px;
        height: 16px;
        background: url("@/assets/checkmark.svg");
        background-size: contain;
      }
    }
  }

  &--optionsWrapper {
    width: 100%;
    display: flex;
    gap: 2rem;
  }

  &--buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;

    button {
      color: inherit;
      padding: 4px 20px;
      border-radius: 3px;
      background: rgba(196, 206, 221, 0.5);
      &:hover {
        background: #b2bccc;
      }
    }
  }
}
</style>
